import React, { useEffect, useState, useMemo } from 'react';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Printer, ArrowLeft, GripVertical } from 'lucide-react';
import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';

const COLUMNS = {
  weekly: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس'],
  term: ['الفصل الأول', 'الفصل الثاني', 'الفصل الثالث'],
  annual: ['سبتمبر-أكتوبر', 'نوفمبر-ديسمبر', 'يناير-فبراير', 'مارس-أبريل', 'مايو-يونيو'],
};

const COLUMNS_EN = {
  weekly: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu'],
  term: ['Term 1', 'Term 2', 'Term 3'],
  annual: ['Sep-Oct', 'Nov-Dec', 'Jan-Feb', 'Mar-Apr', 'May-Jun'],
};

export default function PlansPage() {
  const { t, lang } = useApp();
  const [plans, setPlans] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name_ar: '', kind: 'weekly' });

  useEffect(() => { api.get('/plans').then(r => setPlans(r.data)); }, []);

  const createPlan = async () => {
    if (!form.name_ar) return;
    const { data } = await api.post('/plans', form);
    setPlans([data, ...plans]);
    setActive(data);
    setOpen(false);
    setForm({ name_ar: '', kind: 'weekly' });
  };

  const deletePlan = async (id) => {
    await api.delete(`/plans/${id}`);
    setPlans(plans.filter(p => p.id !== id));
    if (active?.id === id) setActive(null);
  };

  if (active) return <PlanEditor plan={active} onBack={() => { setActive(null); api.get('/plans').then(r => setPlans(r.data)); }} />;

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-8" data-testid="plans-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-black">{t('plans_title')}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-primary" data-testid="new-plan-btn"><Plus className="w-4 h-4 me-1" />{t('plan_new')}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('plan_new')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t('plan_name')}</Label><Input value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} data-testid="plan-name-input" /></div>
              <div>
                <Label>{t('plan_kind')}</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="plan-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">{t('plan_weekly')}</SelectItem>
                    <SelectItem value="term">{t('plan_term')}</SelectItem>
                    <SelectItem value="annual">{t('plan_annual')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={createPlan} className="w-full bg-primary" data-testid="plan-create-btn">{t('save')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {plans.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">{t('no_results')}</CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map(p => (
            <Card key={p.id} className="card-elevate" data-testid={`plan-${p.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <Badge variant="secondary">{t('plan_' + p.kind)}</Badge>
                  <button onClick={() => deletePlan(p.id)} aria-label="delete" className="text-muted-foreground hover:text-destructive" data-testid={`del-plan-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <h3 className="font-bold mb-1">{p.name_ar}</h3>
                <p className="text-xs text-muted-foreground mb-3">{(p.items || []).length} {lang === 'ar' ? 'هدف' : 'goals'}</p>
                <Button size="sm" variant="outline" className="w-full" onClick={() => setActive(p)} data-testid={`open-plan-${p.id}`}>{t('plan_open')}</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </main>
  );
}

function PlanEditor({ plan, onBack }) {
  const { t, lang } = useApp();
  const [items, setItems] = useState(plan.items || []);
  const [pool, setPool] = useState([]);
  const [search, setSearch] = useState('');
  const columns = (lang === 'ar' ? COLUMNS[plan.kind] : COLUMNS_EN[plan.kind]) || [];
  const colKeys = COLUMNS[plan.kind];
  const ArrowIcon = ArrowLeft;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    api.get('/curriculum/goals', { params: { q: search || undefined, limit: 20 } }).then(r => setPool(r.data.items));
  }, [search]);

  const save = async () => {
    await api.put(`/plans/${plan.id}/items`, items);
    toast.success(t('saved'));
  };

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over) return;
    const dropCol = over.id;
    if (!colKeys.includes(dropCol)) return;
    // active.id is `pool-{goal_id}` or `item-{item_id}`
    if (active.id.startsWith('pool-')) {
      const goalId = active.id.replace('pool-', '');
      const g = pool.find(x => x.id === goalId);
      if (!g) return;
      const newItem = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        goal_id: g.id,
        goal_code: g.goal_code,
        title: g.main_goal_ar?.slice(0, 140) || '',
        title_en: g.main_goal_en?.slice(0, 140) || '',
        column: dropCol,
        order: items.filter(i => i.column === dropCol).length,
      };
      setItems([...items, newItem]);
    } else if (active.id.startsWith('item-')) {
      const itemId = active.id.replace('item-', '');
      setItems(items.map(i => i.id === itemId ? { ...i, column: dropCol } : i));
    }
  };

  const removeItem = (id) => setItems(items.filter(i => i.id !== id));

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-6" data-testid="plan-editor">
      <div className="flex items-center justify-between mb-5 print:hidden">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="plan-back"><ArrowIcon className="w-4 h-4 me-1" />{t('prev')}</Button>
          <h1 className="text-2xl font-black">{plan.name_ar}</h1>
          <Badge variant="secondary">{t('plan_' + plan.kind)}</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="plan-print"><Printer className="w-4 h-4 me-1" />{t('plan_print')}</Button>
          <Button size="sm" className="bg-primary" onClick={save} data-testid="plan-save">{t('plan_save')}</Button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <div className="grid lg:grid-cols-4 gap-4">
          <Card className="lg:col-span-1 print:hidden">
            <CardHeader><CardTitle className="text-base">{t('plan_pool')}</CardTitle></CardHeader>
            <CardContent>
              <Input placeholder={t('search_placeholder')} value={search} onChange={(e) => setSearch(e.target.value)} className="mb-3" data-testid="plan-search" />
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {pool.map(g => <PoolItem key={g.id} goal={g} lang={lang} />)}
              </div>
            </CardContent>
          </Card>

          <div className="lg:col-span-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {colKeys.map((c, idx) => (
              <DropColumn key={c} colId={c} title={columns[idx]}>
                {items.filter(i => i.column === c).map(i => (
                  <PlanItemCard key={i.id} item={i} lang={lang} onRemove={() => removeItem(i.id)} />
                ))}
              </DropColumn>
            ))}
          </div>
        </div>
      </DndContext>
    </main>
  );
}

function PoolItem({ goal, lang }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `pool-${goal.id}` });
  const title = lang === 'ar' ? goal.main_goal_ar : (goal.main_goal_en || goal.main_goal_ar);
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-testid={`pool-item-${goal.goal_code}`}
      className={`border border-border rounded-md p-2 bg-white cursor-grab active:cursor-grabbing text-xs ${isDragging ? 'opacity-50' : ''}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <GripVertical className="w-3 h-3 text-muted-foreground shrink-0" aria-hidden="true" />
        <Badge variant="secondary" className="text-[10px] font-mono">{goal.goal_code}</Badge>
      </div>
      <div className="font-medium line-clamp-2">{title}</div>
    </div>
  );
}

function DropColumn({ colId, title, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: colId });
  const { t } = useApp();
  return (
    <div ref={setNodeRef} className={`border-2 ${isOver ? 'border-primary bg-primary/5' : 'border-dashed border-border'} rounded-lg p-3 min-h-[200px] transition-colors`} data-testid={`drop-col-${colId}`}>
      <h3 className="font-bold text-sm mb-3">{title}</h3>
      <div className="space-y-2">
        {children}
        {React.Children.count(children) === 0 && <p className="text-xs text-muted-foreground text-center py-4">{t('plan_drop_here')}</p>}
      </div>
    </div>
  );
}

function PlanItemCard({ item, lang, onRemove }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `item-${item.id}` });
  const title = lang === 'ar' ? item.title : (item.title_en || item.title);
  return (
    <div
      ref={setNodeRef}
      data-testid={`plan-item-${item.id}`}
      className={`bg-primary/10 border border-primary/30 rounded-md p-2 text-xs ${isDragging ? 'opacity-50' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div {...listeners} {...attributes} className="flex-1 cursor-grab active:cursor-grabbing">
          <Badge variant="outline" className="text-[10px] font-mono mb-1">{item.goal_code}</Badge>
          <div className="font-medium line-clamp-2">{title}</div>
        </div>
        <button onClick={onRemove} aria-label="remove" className="text-muted-foreground hover:text-destructive print:hidden">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
